import { CellProperties } from './types';

/**
 * Utility class for table DOM manipulation operations.
 * All methods are static and handle table structure modifications,
 * cell merging/splitting, and property management.
 */
export class TableDOMUtils {
  /**
   * Get the table element containing a cell
   * @param cell - The table cell element
   * @returns The parent table element or null if not found
   */
  static getTableFromCell(cell: HTMLTableCellElement): HTMLTableElement | null {
    return cell.closest('table');
  }

  /**
   * Get the row and column index of a cell within its table
   * @param cell - The table cell element
   * @returns Object with row and col indices, or {row: -1, col: -1} if not found
   */
  static getCellPosition(cell: HTMLTableCellElement): { row: number; col: number } {
    const row = cell.parentElement as HTMLTableRowElement;
    const table = this.getTableFromCell(cell);
    if (!table || !row) return { row: -1, col: -1 };

    const rows = Array.from(table.querySelectorAll('tr'));
    const rowIndex = rows.indexOf(row);
    const cells = Array.from(row.children) as HTMLTableCellElement[];
    const colIndex = cells.indexOf(cell);

    return { row: rowIndex, col: colIndex };
  }

  /**
   * Insert a column at the specified index
   * @param table - The table element
   * @param colIndex - The column index to insert at
   * @param position - Whether to insert 'before' or 'after' the specified column
   */
  static insertColumn(table: HTMLTableElement, colIndex: number, position: 'before' | 'after'): void {
    const rows = table.querySelectorAll('tr');
    const actualIndex = position === 'after' ? colIndex + 1 : colIndex;

    rows.forEach(row => {
      const isHeader = row.closest('thead') !== null;
      const cell = document.createElement(isHeader ? 'th' : 'td');
      cell.style.cssText = 'border: 1px solid #ddd; padding: 8px; min-width: 50px;';

      const referenceCell = row.children[colIndex] as HTMLTableCellElement;
      if (referenceCell) {
        if (position === 'after') {
          referenceCell.after(cell);
        } else {
          referenceCell.before(cell);
        }
      } else {
        row.appendChild(cell);
      }
    });
  }

  /**
   * Delete a column at the specified index
   * @param table - The table element
   * @param colIndex - The column index to delete
   * @returns false if this is the last column (table should be deleted), true otherwise
   */
  static deleteColumn(table: HTMLTableElement, colIndex: number): boolean {
    const rows = table.querySelectorAll('tr');
    const firstRow = rows[0];
    if (!firstRow || firstRow.children.length <= 1) {
      // Can't delete last column - delete entire table instead
      return false;
    }

    rows.forEach(row => {
      const cell = row.children[colIndex];
      if (cell) {
        // Handle colspan adjustments
        const colspan = parseInt(cell.getAttribute('colspan') || '1');
        if (colspan > 1) {
          cell.setAttribute('colspan', (colspan - 1).toString());
        } else {
          cell.remove();
        }
      }
    });

    return true;
  }

  /**
   * Insert a row at the specified index
   * @param table - The table element
   * @param rowIndex - The row index to insert at
   * @param position - Whether to insert 'before' or 'after' the specified row
   */
  static insertRow(table: HTMLTableElement, rowIndex: number, position: 'before' | 'after'): void {
    const rows = Array.from(table.querySelectorAll('tr'));
    const referenceRow = rows[rowIndex];
    if (!referenceRow) return;

    const colCount = referenceRow.children.length;
    const newRow = document.createElement('tr');

    for (let i = 0; i < colCount; i++) {
      const cell = document.createElement('td');
      cell.style.cssText = 'border: 1px solid #ddd; padding: 8px; min-width: 50px;';
      newRow.appendChild(cell);
    }

    if (position === 'after') {
      referenceRow.after(newRow);
    } else {
      referenceRow.before(newRow);
    }
  }

  /**
   * Delete a row at the specified index
   * @param table - The table element
   * @param rowIndex - The row index to delete
   * @returns false if this is the last row (table should be deleted), true otherwise
   */
  static deleteRow(table: HTMLTableElement, rowIndex: number): boolean {
    const rows = table.querySelectorAll('tr');
    if (rows.length <= 1) {
      // Can't delete last row - delete entire table instead
      return false;
    }

    const row = rows[rowIndex];
    if (row) {
      // Handle rowspan adjustments for cells in this row
      const cells = Array.from(row.children) as HTMLTableCellElement[];
      cells.forEach(cell => {
        const rowspan = parseInt(cell.getAttribute('rowspan') || '1');
        if (rowspan > 1) {
          // Move cell to next row with adjusted rowspan
          const nextRow = rows[rowIndex + 1];
          if (nextRow) {
            cell.setAttribute('rowspan', (rowspan - 1).toString());
            nextRow.insertBefore(cell, nextRow.firstChild);
          }
        }
      });

      row.remove();
    }

    return true;
  }

  /**
   * Toggle header row on/off
   * Converts first row to/from header row with thead/th elements
   * @param table - The table element
   */
  static toggleHeaderRow(table: HTMLTableElement): void {
    const thead = table.querySelector('thead');
    const tbody = table.querySelector('tbody') || table;

    if (thead) {
      // Convert header to regular row
      const headerRow = thead.querySelector('tr');
      if (headerRow) {
        const cells = headerRow.querySelectorAll('th');
        cells.forEach(th => {
          const td = document.createElement('td');
          td.innerHTML = th.innerHTML;
          // Copy attributes
          Array.from(th.attributes).forEach(attr => {
            td.setAttribute(attr.name, attr.value);
          });
          th.replaceWith(td);
        });

        tbody.insertBefore(headerRow, tbody.firstChild);
        thead.remove();
      }
    } else {
      // Convert first row to header
      const firstRow = tbody.querySelector('tr');
      if (firstRow) {
        const cells = firstRow.querySelectorAll('td');
        cells.forEach(td => {
          const th = document.createElement('th');
          th.innerHTML = td.innerHTML;
          // Copy attributes
          Array.from(td.attributes).forEach(attr => {
            th.setAttribute(attr.name, attr.value);
          });
          // Add header styling
          th.style.fontWeight = 'bold';
          th.style.backgroundColor = '#f5f5f5';
          td.replaceWith(th);
        });

        const newThead = document.createElement('thead');
        newThead.appendChild(firstRow);
        table.insertBefore(newThead, tbody);
      }
    }
  }

  /**
   * Merge cells in a direction
   * @param cell - The cell to merge from
   * @param direction - Direction to merge: 'up', 'down', 'left', or 'right'
   * @returns true if merge was successful, false otherwise
   */
  static mergeCells(cell: HTMLTableCellElement, direction: 'up' | 'down' | 'left' | 'right'): boolean {
    const table = this.getTableFromCell(cell);
    if (!table) return false;

    const { row: rowIndex, col: colIndex } = this.getCellPosition(cell);
    const rows = Array.from(table.querySelectorAll('tr'));

    if (direction === 'right') {
      const row = rows[rowIndex];
      const nextCell = row.children[colIndex + 1] as HTMLTableCellElement;
      if (nextCell) {
        const colspan = parseInt(cell.getAttribute('colspan') || '1');
        cell.setAttribute('colspan', (colspan + 1).toString());
        cell.innerHTML += nextCell.innerHTML;
        nextCell.remove();
        return true;
      }
    } else if (direction === 'left') {
      const row = rows[rowIndex];
      const prevCell = row.children[colIndex - 1] as HTMLTableCellElement;
      if (prevCell) {
        const colspan = parseInt(prevCell.getAttribute('colspan') || '1');
        prevCell.setAttribute('colspan', (colspan + 1).toString());
        prevCell.innerHTML += cell.innerHTML;
        cell.remove();
        return true;
      }
    } else if (direction === 'down') {
      const nextRow = rows[rowIndex + 1];
      if (nextRow) {
        const nextCell = nextRow.children[colIndex] as HTMLTableCellElement;
        if (nextCell) {
          const rowspan = parseInt(cell.getAttribute('rowspan') || '1');
          cell.setAttribute('rowspan', (rowspan + 1).toString());
          cell.innerHTML += nextCell.innerHTML;
          nextCell.remove();
          return true;
        }
      }
    } else if (direction === 'up') {
      const prevRow = rows[rowIndex - 1];
      if (prevRow) {
        const prevCell = prevRow.children[colIndex] as HTMLTableCellElement;
        if (prevCell) {
          const rowspan = parseInt(prevCell.getAttribute('rowspan') || '1');
          prevCell.setAttribute('rowspan', (rowspan + 1).toString());
          prevCell.innerHTML += cell.innerHTML;
          cell.remove();
          return true;
        }
      }
    }

    return false;
  }

  /**
   * Split a merged cell
   * @param cell - The cell to split
   * @param direction - Direction to split: 'horizontal' (colspan) or 'vertical' (rowspan)
   * @returns true if split was successful, false otherwise
   */
  static splitCell(cell: HTMLTableCellElement, direction: 'horizontal' | 'vertical'): boolean {
    const colspan = parseInt(cell.getAttribute('colspan') || '1');
    const rowspan = parseInt(cell.getAttribute('rowspan') || '1');

    if (direction === 'horizontal' && colspan > 1) {
      // Split horizontally (reduce colspan, add cell to the right)
      cell.setAttribute('colspan', (colspan - 1).toString());
      const newCell = document.createElement(cell.tagName.toLowerCase() as 'td' | 'th');
      newCell.style.cssText = cell.style.cssText;
      if (rowspan > 1) {
        newCell.setAttribute('rowspan', rowspan.toString());
      }
      cell.after(newCell);
      return true;
    } else if (direction === 'vertical' && rowspan > 1) {
      // Split vertically (reduce rowspan, add cell below)
      cell.setAttribute('rowspan', (rowspan - 1).toString());

      const table = this.getTableFromCell(cell);
      if (!table) return false;

      const { row: rowIndex, col: colIndex } = this.getCellPosition(cell);
      const rows = Array.from(table.querySelectorAll('tr'));
      const targetRow = rows[rowIndex + 1];

      if (targetRow) {
        const newCell = document.createElement(cell.tagName.toLowerCase() as 'td' | 'th');
        newCell.style.cssText = cell.style.cssText;
        if (colspan > 1) {
          newCell.setAttribute('colspan', colspan.toString());
        }

        // Insert at correct position
        const referenceCell = targetRow.children[colIndex];
        if (referenceCell) {
          targetRow.insertBefore(newCell, referenceCell);
        } else {
          targetRow.appendChild(newCell);
        }
        return true;
      }
    }

    return false;
  }

  /**
   * Check if cells can be merged in a direction
   * @param cell - The cell to check
   * @param direction - Direction to check: 'up', 'down', 'left', or 'right'
   * @returns true if merge is possible, false otherwise
   */
  static canMerge(cell: HTMLTableCellElement, direction: 'up' | 'down' | 'left' | 'right'): boolean {
    const table = this.getTableFromCell(cell);
    if (!table) return false;

    const { row: rowIndex, col: colIndex } = this.getCellPosition(cell);
    const rows = Array.from(table.querySelectorAll('tr'));
    const row = rows[rowIndex];

    if (direction === 'right') {
      return colIndex < row.children.length - 1;
    } else if (direction === 'left') {
      return colIndex > 0;
    } else if (direction === 'down') {
      return rowIndex < rows.length - 1;
    } else if (direction === 'up') {
      return rowIndex > 0;
    }

    return false;
  }

  /**
   * Apply cell properties to a cell
   * @param cell - The cell to apply properties to
   * @param properties - The properties to apply
   */
  static applyCellProperties(cell: HTMLTableCellElement, properties: CellProperties): void {
    // Border
    if (properties.borderStyle !== 'none') {
      cell.style.borderStyle = properties.borderStyle;
      cell.style.borderColor = properties.borderColor;
      cell.style.borderWidth = properties.borderWidth;
    } else {
      cell.style.border = 'none';
    }

    // Background
    cell.style.backgroundColor = properties.backgroundColor;

    // Dimensions
    if (properties.width !== 'auto') {
      cell.style.width = properties.width;
    }
    if (properties.height !== 'auto') {
      cell.style.height = properties.height;
    }
    cell.style.padding = properties.padding;

    // Alignment
    cell.style.textAlign = properties.textAlign;
    cell.style.verticalAlign = properties.verticalAlign;
  }

  /**
   * Extract cell properties from a cell
   * @param cell - The cell to extract properties from
   * @returns The extracted cell properties
   */
  static extractCellProperties(cell: HTMLTableCellElement): CellProperties {
    const computedStyle = window.getComputedStyle(cell);

    return {
      borderStyle: (computedStyle.borderStyle as any) || 'solid',
      borderColor: computedStyle.borderColor || '#ddd',
      borderWidth: computedStyle.borderWidth || '1px',
      backgroundColor: computedStyle.backgroundColor || 'transparent',
      width: cell.style.width || 'auto',
      height: cell.style.height || 'auto',
      padding: computedStyle.padding || '8px',
      textAlign: (computedStyle.textAlign as any) || 'left',
      verticalAlign: (computedStyle.verticalAlign as any) || 'middle'
    };
  }
}
